import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { ValueFormElement } from 'elements/_shared/formElement.js'
import { formFieldValidationTypes } from 'elements/form-field/form-field.js'
import { CSSResultGroup, html, PropertyValues } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { style } from './text-input.style.js'


initializeIconsByRole({
  'cse-text-input.clear': { name: 'xmark', style: 'light' },
})

export const textInputTypes = ['email', 'search', 'tel', 'text', 'number'] as const
export type textInputTypesType = typeof textInputTypes


@customElement('cse-text-input')
export class CSETextInput extends ValueFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'id' })
  _id = ''

  @property({ type: String })
  name = ''

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Number })
  min: number = 0

  @property({ type: Number })
  max?: number

  @property({ type: Number })
  step?: number

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean })
  clearable = false

  @property({ type: String })
  placeholder = ''

  @property({ type: String })
  valid: formFieldValidationTypes[number] = 'none'

  @query('input')
  input!: HTMLInputElement

  @property({ type: String, attribute: 'required-text' })
  requiredText?: string

  @property({ type: String, attribute: 'type' })
  inputType: textInputTypesType[number] = 'text'

  @property({ type: Boolean })
  override autofocus: boolean = false

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (['value', 'required'].some((prop: string) => changedProperties.has(prop))) { this.checkRequired() }
  }

  private setValue = () => {
    this.value = this.input.value
    this.emit(this.value)
  }

  private setValueSilent = () => {
    this.value = this.input.value
    // No emit - just update internal state
  }

  private onChange() {
    this.setValue()
  }

  private onInput() {
    if (this.inputType === 'number') {
      const value = parseFloat(this.input.value)
      const min = parseFloat(this.input.min) || 0
      const max = parseFloat(this.input.max)
      const step = parseFloat(this.input.step) || 1

      const steppedValue = Math.floor(value / step) * step

      if (min && steppedValue < min) {
        this.input.value = min.toString()
      } else if (max && steppedValue > max) {
        this.input.value = max.toString()
      } else {
        this.input.value = steppedValue.toString()
      }
    }
    this.setValueSilent()
    this.checkRequired()
  }

  private checkRequired() {
    if (this.required && this.input.value.trim() === '') {
      this.internals.setValidity({
        valueMissing: true,
      }, this.requiredText || 'This field is required', this.input)
    } else {
      this.internals.setValidity({})
    }
  }

  private handleClearButtonClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (this.disabled) { return }

    this.input.value = ''
    this.value = ''
    this.emit('')
    this.checkRequired()
    this.dispatchEvent(new CSETextInputClearedEvent(this))
  }

  private renderClearButton = () => html`
    <cse-icon-button
      variant="secondary"
      size="small"
      @click="${this.handleClearButtonClick}"
      aria-label="${t('clear_text', { ns: 'cse-text-input', default: 'Clear text' })}"
    >
      <cse-icon icon-role="cse-text-input.clear"></cse-icon>
    </cse-icon-button>
  `

  shouldRenderClearButton() {
    if (this.disabled || !this.clearable || this.inputType === 'number') {
      return false
    }

    return this.value !== '' && !this.required
  }

  override focus() {
    this.input.focus()
  }

  override render() {
    return html`
      <div class="${classMap({
        'cse-text-input': true,
        [`valid-${this.valid}`]: true,
        'disabled': this.disabled,
      })}">
        <slot class="cse-text-input-prefix" name="prefix"></slot>
        <input
          type="${this.inputType}"
          id="${this._id}"
          name="${this.name}"
          value="${this.value}"
          ?required="${this.required}"
          ?disabled="${this.disabled}"
          ?autofocus="${this.autofocus}"
          placeholder="${this.placeholder}"
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          step=${ifDefined(this.step)}
          @change="${this.onChange}"
          @input=${this.onInput}
        />
        <slot class="cse-text-input-suffix" name="suffix"></slot>
        ${this.shouldRenderClearButton() ? this.renderClearButton() : null}
      </div>
    `
  }
}

export class CSETextInputClearedEvent extends Event {
  static type = 'cse-text-input-cleared' as const

  public textInput: CSETextInput

  constructor(textInput: CSETextInput) {
    super(CSETextInputClearedEvent.type, { bubbles: true, composed: true })

    this.textInput = textInput
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-text-input': CSETextInput
  }
  interface GlobalEventHandlersEventMap {
    [CSETextInputClearedEvent.type]: CSETextInputClearedEvent,
  }
}
